import React from 'react';
import { Spin, Result, Button } from 'antd';
import { LoadingOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import AuthLogin from './AuthLogin';
import OrganizationSelectModal from './OrganizationSelectModal';
import { getString } from '../utils/translations';

const ProtectedRoute = ({ children }) => {
  const {
    user,
    organization,
    loading,
    needsOrganizationSelection,
    hasVideoProcessingAccess,
    signOut
  } = useAuth();

  // Show loading spinner while authentication is being determined
  if (loading) {
    return (
      <div style={{ 
        height: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        flexDirection: 'column'
      }}>
        <Spin 
          indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
          size="large"
        />
        <div style={{ marginTop: 16, fontSize: 16, color: '#666' }}>
          {getString('pipeline.protected.authenticating', 'Authenticating with SDK Backend...')}
        </div>
      </div>
    );
  }

  // Show login page if user is not authenticated
  if (!user) {
    return <AuthLogin />;
  }

  if (needsOrganizationSelection && !organization) {
    return <OrganizationSelectModal open closable={false} />;
  }

  if (!organization) {
    return <AuthLogin />;
  }

  // Show access denied if organization doesn't have video processing enabled
  if (!hasVideoProcessingAccess) {
    return (
      <Result
        status="403"
        title={getString('pipeline.protected.access_required', 'Video Processing Access Required')}
        subTitle={`${organization.name}: ${getString('pipeline.protected.access_required_desc', 'Organization does not have video processing enabled. Please contact your administrator.')}`}
        icon={<ExclamationCircleOutlined style={{ color: '#faad14' }} />}
        extra={
          <div style={{ textAlign: 'center' }}>
            <Button type="primary" onClick={signOut}>
              {getString('pipeline.nav.sign_out', 'Sign Out')}
            </Button>
            <div style={{ marginTop: 16, fontSize: 14, color: '#666' }}>
              <strong>Organization:</strong> {organization.name} (ID: {organization.id})<br />
              <strong>Current User:</strong> {user.email}<br />
              <strong>Video Processing:</strong> {hasVideoProcessingAccess ? 'Enabled' : 'Disabled'}
            </div>
          </div>
        }
      />
    );
  }

  // User is authenticated and has access - show the protected content
  return children;
};

export default ProtectedRoute;
